import React, {
  ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import dayjs from 'dayjs'
import moment from 'moment'
import {
  Avatar,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select
} from 'antd'
import { SettingOutlined } from '@ant-design/icons'
import { useLocalStorage } from '@/utils/useSparrowLocalStorage'
import { contexts } from '@/domains'
import {
  DeliveryConfigOption,
  DeliveryItem,
  SubmitDeliveryItem
} from '@/domains/hooks/useDelivery'
import useFormatData from '@/domains/hooks/useFormatData'
import DeliveryConfigModal, {
  ImperativeHandle as DeliveryConfigModalImperativeHandle
} from './DeliveryConfigModal'
// import ParticipantItem from './ParticipantItem';
import ParticipantCard from './ParticipantCard'
import { UserInfo } from '@/domains/hooks/useUser'
import TextArea from 'antd/lib/input/TextArea'
import { action } from 'mobx';
import EnumConfig from '@/components/EnumConfig/EnumConfig'
import { EnumConfigOption, globalStore } from '@/common'
import { SNumber } from '@/common/model'
import { projectPageModel } from '@/project/model';
import ProjectSelect from '@/components/ProjectSelect/ProjectSelect';
import { displayUserAvatar, displayUserName } from '@/utils/userDisplay';

type Props = {
  mode: 'create' | 'update' | 'delete',
  onClose:()=>void
}

type DeliveryFormData = {
  id?: number
  name?: string
  date?: moment.Moment
  taskSize?: 0 | 1 | 2 | 3
  meetingName?: number
  type?: number
  projectId?: string
  projectName?: string
  status?: number
  taskUrl?: string
  remarks?: string
  userIds?: string[]
}

type Store = {
  visible: boolean
  formData: DeliveryFormData
  isLoading: boolean 
  currentConfigs:EnumConfigOption[], 
  configTypeVisible: boolean
  configType: 'meeting' | 'type' | 'bizType' | 'status' | ''
  configNumberType:SNumber
  configDataSource: DeliveryConfigOption[]
}

export type ImperativeHandle = {
  open: (record?: DeliveryItem) => void
  close: () => void
}

const { DeliveryContext } = contexts

const TaskSizeOptions = [
  {
    label: 'S',
    value: 0
  },
  {
    label: 'M',
    value: 1
  },
  {
    label: 'L',
    value: 2
  },
  {
    label: 'XL',
    value: 3
  }
]

function DeliveryFormModal(props: Props, ref: ForwardedRef<ImperativeHandle>) {
  // const [token] = useLocalStorage<string>({
  //   key: 'token'
  // })
  const userInfoData = useLocalStorage({ key: 'userInfo' });
  const userInfo = userInfoData[0] as UserInfo; 
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
 
  const { onlyFridayAvailable } = useFormatData()
  const {
    currentDate,
    userInfos,
    userRecord,
    handleCreateDeliveryItem,
    handleCreateDeliveryConfigType,
    handleUpdateDeliveryItems
  } = DeliveryContext.useContainer()

  const store = useLocalObservable<Store>(() => ({
    visible: false,
    formData: {
      id: undefined,
      name: undefined,
      date: currentDate ? moment(currentDate) : undefined,
      meetingName: undefined,
      type: undefined, 
      projectId:undefined,
      projectName:undefined,
      status: undefined,
      taskUrl: '',
      remarks: undefined,
      userIds: []
    },
    isLoading: false,  
    configTypeVisible: false,
    currentConfigs:null,
    configType: '',
    configNumberType: 0,
    configDataSource: []
  }))

  const handleFormDataChange = (
    propName: string,
    value: string | string[] | moment.Moment | number | null
  ): void => {
    if (propName === 'userInfos') {
      store.formData.userIds.push(value[0])
    } else {
      store.formData[propName] = value
    }
  }

  const handleConfirm = async () => {
    if (!store.formData.name) {
      message.warning('请输入交付项名称', 1)
      return
    }
    if (store.formData.taskSize === undefined) {
      message.warning('请输入项目规模', 1)
      return
    }
    if (!store.formData.meetingName) {
      message.warning('请输入跟进晨会名称', 1)
      return
    }
    if (!store.formData.type) {
      message.warning('请选择交付类型', 1)
      return
    } 
    if (!store.formData.status) {
      message.warning('请选择交付项状态', 1)
      return
    }
    if (
      props.mode !== 'create' &&
      (!store.formData.userIds || store.formData.userIds.length <= 0)
    ) {
      message.warning('请选择交付项执参加者', 1)
      return
    }

    const payload: SubmitDeliveryItem = {
      finishDate: store.formData.date
        ? moment(store.formData.date).format('YYYY-MM-DD 00:00:00')
        : null,
      morningMeetingId: store.formData.meetingName,
      userId: userInfo.id,
      orderNum: 1,
      remarks: store.formData.remarks,
      taskName: store.formData.name,
      taskSize: store.formData.taskSize,
      taskStatusId: store.formData.status,
      taskTypeId: store.formData.type,
      taskUrl: store.formData.taskUrl,
      userList: (store.formData.userIds ?? []).map(
        (value: string): { userId: string; userType: number } => {
          return {
            userId: value,
            userType: 1
          }
        }
      ),
      projectId:
        store.formData.projectId != null && store.formData.projectId !== ''
          ? String(store.formData.projectId)
          : null,
      projectName: store.formData.projectName ?? null,
    }

    let res
    let msg

    store.isLoading = true

    if (props.mode === 'create') {
      res = await handleCreateDeliveryItem(payload).finally(() => {
        store.isLoading = false
      })
      msg = res ? '添加成功' : '添加失败'
    } else {
      ;(payload as SubmitDeliveryItem & { id: number }).id = store.formData.id
      res = await handleUpdateDeliveryItems({
        deliveryWeekTaskList: [payload as SubmitDeliveryItem & { id: number }]
      }).finally(() => {
        store.isLoading = false
      })
      msg = res ? '更新成功' : '更新失败'
    }

    if (!res) {
      message.error(msg, 1)
      return
    }

    setTimeout(() => {
      message.success(msg, 1)
    }, 500)

    handleCancel()
  }

  const handleCancel = () => {
    store.visible = false
    store.formData = {
      name: undefined,
      date: currentDate ? moment(currentDate) : undefined,
      meetingName: undefined,
      projectId:undefined,
      projectName:undefined,
      type: undefined, 
      status: undefined,
      remarks: undefined,
      userIds: []
    } 
    props.onClose()
  }

  const handleCreateConfig = (
    type: 'meeting' | 'type' | 'bizType' | 'status'
  ): void => {
    store.configType = type

    switch (type) {
      case 'meeting':
        store.configDataSource = globalStore.enumConfigs.meetingOptions
        store.configNumberType = 1
        break
      case 'type':
        store.configDataSource = globalStore.enumConfigs.taskTypeOptions
        store.configNumberType = 5
        break
      case 'bizType':
        store.configDataSource = globalStore.enumConfigs.bizLineOptions
        store.configNumberType = 2
        break
      case 'status':
        store.configDataSource = globalStore.enumConfigs.taskStatusOptions
        store.configNumberType = 4
        break
    }

    store.configTypeVisible = true;
 
  }

  const moveListItem = (dragIndex: number, hoverIndex: number) => {
     const { userIds: userInfos } = store.formData;
  
      // 取出被拖动的元素
      const draggedItem = userInfos[dragIndex];
      
      // 从原数组中移除被拖动的元素
      const updatedUserInfos = userInfos.filter((item, index) => index !== dragIndex);
      
      // 将被拖动的元素插入新的位置
      updatedUserInfos.splice(hoverIndex, 0, draggedItem);
      
      // 更新状态
      store.formData.userIds = updatedUserInfos; 
    
  };

  const onDrop = (dragIndex: number, hoverIndex: number) => {
    const { userIds: userInfos } = store.formData;
    const updatedUserInfos = [...userInfos];
    const [draggedItem] = updatedUserInfos.splice(dragIndex, 1);
    updatedUserInfos.splice(hoverIndex, 0, draggedItem);
    store.formData.userIds = updatedUserInfos; 
  };

  useImperativeHandle(ref, () => ({
    open(record?: DeliveryItem) {
      if (store.visible) return

      if (record && record.id) {
        store.formData.id = record.id
      }
      if (record && record.taskName) {
        store.formData.name = record.taskName
      }
      if (record && record.taskSize !== undefined) {
        store.formData.taskSize = record.taskSize
      } 
      if (record && record.morningMeetingId) {
        store.formData.meetingName = record.morningMeetingId
      }
      if (record && record.taskTypeId) {
        store.formData.type = record.taskTypeId
      }
      if (record && record.taskStatusId) {
        store.formData.status = record.taskStatusId
      }
      if (record && record.remarks) {
        store.formData.remarks = record.remarks
      }
      if (record && record.taskUrl) {
        store.formData.taskUrl = record.taskUrl
      }
      if (record && record.userList && record.userList.length > 0) {
        store.formData.userIds = record.userList.map(
          (userInfoItem): string => `${userInfoItem.userId}`
        )
        // store.userInfos = record.userList
      }
      if (record?.projectId) {
        store.formData.projectId = String(record.projectId)
      }
      if (record?.projectName) {
        store.formData.projectName = record.projectName
      }

      store.visible = true
    },
    close() {
      if (!store.visible) return 
      store.visible = false
      props.onClose();
    }
  }))

  useEffect((): void => {
    store.formData.date = currentDate ? moment(currentDate) : undefined
  }, [currentDate])

 
  return (
    <Modal 
      width={700}
      title={`${props.mode === 'create' ? '添加' : '编辑'}交付项`}
      open={store.visible}
      centered
      okButtonProps={{
        size: 'middle',
        disabled: store.isLoading,
        loading: store.isLoading
      }}
      cancelButtonProps={{
        size: 'middle'
      }}
      onOk={handleConfirm}
      onCancel={handleCancel}
      wrapClassName="add-deliver"
    >
      <DndProvider backend={HTML5Backend}>
        <Row>
          <Col span={24}>
           <div style={{marginBottom: '0px'}}>
                <Input
                  value={store.formData.name}
                  placeholder="请总结交付项的名称"
                  className='title-input'
                  onChange={(e) => handleFormDataChange('name', e.target.value)}
                />
            </div>
          </Col>
        </Row>
        <Row>
          <Col span={12}> 
            <Form labelCol={{ span: 5 }} wrapperCol={{ span: 15 }} style={{marginTop:'10px', borderRight:'1px solid #ddd'}}>
              <Form.Item label='规模' required>
                <Select
                  value={store.formData.taskSize}
                  placeholder="请选择任务规模"
                  onChange={(e) => handleFormDataChange('taskSize', e)} 
                  style={{ width: '100%', height:'30px'}}
                >
                  {TaskSizeOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item  label='截止' >
                <DatePicker
                  placeholder="请选择截止日期"
                  style={{ width: '100%' }}
                  disabledDate={onlyFridayAvailable}
                  inputReadOnly
                  value={
                    store.formData.date ? dayjs(store.formData.date) : undefined
                  }
                  format="YYYY-MM-DD ddd"
                  onChange={(e) => {
                    if (e != null && e.day() !== 5) {
                      return
                    }
                    handleFormDataChange(
                      'date',
                      e ? moment(e.format('YYYY-MM-DD')) : null,
                    )
                  }}
                />
              </Form.Item> 
              <Form.Item label='晨会' required>
                <div className="flex items-center" style={{ width: '100%' }}>
                  <Select 
                    value={store.formData.meetingName}
                    placeholder="请选择跟进晨会" 
                    className='no-border-select'
                    onChange={(e) => handleFormDataChange('meetingName', e)}
                  >
                    {globalStore.enumConfigs.meetingOptions
                      .filter((opt) => opt.deleteFlag === 0)
                      .map((option: DeliveryConfigOption) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                  <Button
                    className="flex-shrink-0"
                    style={{ marginLeft: 5 , border:'none'}} 
                    icon={<SettingOutlined />} 
                    onClick={() => {
                      handleCreateConfig('meeting')
                    }}
                  />
                </div>
              </Form.Item>
              <Form.Item label='类型' required>
                <div className="flex items-center">
                  <Select
                    className='no-border-select'
                    value={store.formData.type}
                    placeholder="请选择交付类型"
                    onChange={(e) => handleFormDataChange('type', e)}
                  >
                    {globalStore.enumConfigs.taskTypeOptions
                      .filter((opt) => opt.deleteFlag === 0)
                      .map((option: DeliveryConfigOption) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                  <Button
                    className="flex-shrink-0"
                    style={{ marginLeft: 5 , border:'none'}} 
                    icon={<SettingOutlined />}
                    // icon={<PlusOutlined />}
                    onClick={() => {
                      handleCreateConfig('type')
                    }}
                  />
                </div>
              </Form.Item>
              
              <Form.Item label='状态' required>
                <div className="flex items-center">
                  <Select
                    value={store.formData.status}
                    placeholder="请设置交付状态"
                    className='no-border-select'
                    onChange={(e) => handleFormDataChange('status', e)}
                  >
                    {globalStore.enumConfigs.taskStatusOptions
                      .filter((opt) => opt.deleteFlag === 0)
                      .map((option: DeliveryConfigOption) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                  <Button
                    className="flex-shrink-0"
                    style={{ marginLeft: 5 , border:'none'}} 
                    icon={<SettingOutlined />}
                    // icon={<PlusOutlined />}
                    onClick={() => {
                      handleCreateConfig('status')
                    }}
                  />
                </div>
              </Form.Item> 
            </Form>
          </Col>
          <Col span={12} style={{paddingLeft:40,paddingTop:10}}>
            <Form className='member-select-form'>
              <Form.Item
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 20 }}
                className='add-member-select'
              >
                <Select
                  showSearch
                  className='member-select' 
                  placeholder="添加参与人"
                  filterOption={(input, option) => {
                    const u = userRecord[String(option?.value)];
                    if (!u) {
                      return false;
                    }
                    return `${displayUserName(u)}${u.userNamePinyin ?? ''}${u.nicknamePinyin ?? ''}`
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                  value={null}
                  // onSearch={handleSearch}
                  onChange={(e) => {
                    handleFormDataChange('userInfos', [e])
                  }}
                >
                  {userInfos && userInfos
                    .filter(
                      (userInfo) =>{
                        // debugger
                        return !store.formData.userIds.includes(userInfo.id)
                      }
                    )
                    .map((userInfo) => {
                      return(
                      <Select.Option
                        key={userInfo.id}
                        value={userInfo.id}
                      >
                        <span className="flex items-center gap-2">
                          <Avatar size="small" src={displayUserAvatar(userInfo)}>
                            {!displayUserAvatar(userInfo) && displayUserName(userInfo)
                              ? displayUserName(userInfo).slice(0, 1)
                              : null}
                          </Avatar>
                          {displayUserName(userInfo) || userInfo.id}
                        </span>
                      </Select.Option>
                    )})}
                </Select>
              </Form.Item>
              <Form.Item
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 20 }}
              >
                <div className="member-container">
                  {store.formData.userIds.map((item, index) => (
                    <ParticipantCard
                      key={item}
                      index={index}
                      userId={item}
                      onDrop={onDrop}
                      moveListItem={moveListItem}
                      onRemove={() => {
                        store.formData.userIds =
                          store.formData.userIds.filter(
                            (userId: string): boolean => {
                              return userId !== item
                            }
                          )
                      }}
                      isDragging={index === draggingIndex}
                      onDragStart={() => setDraggingIndex(index)}
                      onDragEnd={() => setDraggingIndex(null)}
                    />
                  ))}
                </div>
              </Form.Item>
            </Form>
          </Col>
        </Row>
        <Row className='link-row'>
          <Col span={24}>
              <Form.Item label='项目'>
                <div className="flex items-center project-select-container ">
                  <ProjectSelect 
                    defaultProjectName={store.formData.projectName}
                    onSelect={(val, name) => {
                      handleFormDataChange('projectId', val)
                      handleFormDataChange('projectName', name ?? null)
                    }}
                  >
                  </ProjectSelect>
                </div>
              </Form.Item>
          </Col>
          <Col span={24}>
              <Form.Item label='文档'>
                  <Input
                        value={store.formData.taskUrl}
                        className='add-link'
                        placeholder="添加文档链接"
                        onChange={(e) => {
                          handleFormDataChange('taskUrl', e.target.value)
                        }}
                    />
              </Form.Item> 
          </Col>
        </Row>
        <Row className='desc-row'>
          <Col span={24}>
              <Form.Item label='说明' className='desc-textarea'>
                  <TextArea
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    value={store.formData.remarks}
                    className='add-desc'
                    placeholder="添加更多说明" 
                    onChange={(e) =>
                      handleFormDataChange('remarks', e.target.value)
                    }
                  />
              </Form.Item> 
          </Col>
        </Row>
        {/* <DeliveryConfigModal ref={subRef} configType={store.configType} />  */}
        <EnumConfig 
          visible={store.configTypeVisible}
          options={store.configDataSource} 
          type = {store.configNumberType}
          onClose={()=>{store.configTypeVisible=false}}
          />
      </DndProvider>
    </Modal>
  )
}

export default observer<Props, ImperativeHandle>(DeliveryFormModal, {
  forwardRef: true
})
// export default forwardRef<ImperativeHandle, Props>(observer(DeliveryFormModal))
